import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart3, TrendingUp, Users,
  MessageSquare, ArrowLeft, Loader2, CheckCircle,
  AlertCircle, Lightbulb, Filter, ChevronDown, ChevronRight, X
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';
import '../styles/personaInterview.css';
import '../styles/surveyDashboard.css';

function SurveyAnalysisDashboard() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('text');
  const [selectedTextQuestion, setSelectedTextQuestion] = useState(0);
  const [selectedScaleQuestion, setSelectedScaleQuestion] = useState(0);
  const [selectedSelectQuestion, setSelectedSelectQuestion] = useState(0);
  const [expandedQuotes, setExpandedQuotes] = useState({});
  const [showFilterBar, setShowFilterBar] = useState(false);

  // Format question text with newlines
  const formatQuestionText = (text) => {
    if (!text) return '';
    return text.replace(/\\n/g, '\n');
  };

  // Markdown formatting function
  const formatMarkdown = (md) => {
    if (!md) return '';

    // Clean up the text: remove wrapping quotes and convert escaped newlines to actual newlines
    let cleanedMd = md
      .replace(/^"/, '')            // Remove leading quote
      .replace(/"$/, '')            // Remove trailing quote
      .replace(/\\"/g, '"')         // Unescape quotes
      .replace(/\\n\\n/g, '\n\n')   // Convert double escaped newlines to actual double newlines
      .replace(/\\n/g, '\n');       // Convert single escaped newlines to actual newlines

    const blocks = cleanedMd.trim().split(/\n{2,}/);
    const html = blocks.map(block => {
      if (/^###\s/.test(block)) return `<h3>${block.slice(4)}</h3>`;
      if (/^##\s/.test(block))  return `<h2>${block.slice(3)}</h2>`;
      if (/^#\s/.test(block))   return `<h1>${block.slice(2)}</h1>`;

      if (/^- /.test(block)) {
        const items = block
          .split('\n')
          .map(line => `<li>${line.replace(/^- /, '')}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }

      const inner = block
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
        // Handle quoted text - use dark grey color instead of bold/italic
        .replace(/"([^"]+?)"/g, '<span style="color: #475569;">"$1"</span>')
        .replace(/\n/g, '<br />');

      return `<p>${inner}</p>`;
    }).join('');

    return html;
  };

  // Demographic segmentation state
  const [demographicFilters, setDemographicFilters] = useState({
    location_type: 'all',
    age_range: 'all',
    income_bracket: 'all',
    profile_name: 'all'
  });
  const [breakdownDimension, setBreakdownDimension] = useState('');
  const [customGroups, setCustomGroups] = useState({});
  const [expandedSegmentRows, setExpandedSegmentRows] = useState({});

  useEffect(() => {
    fetchSurveyAnalysis();
  }, [surveyId]);

  const fetchSurveyAnalysis = async () => {
    try {
      setLoading(true);
      console.log('Fetching survey:', surveyId);

      const response = await fetch(`https://n8n.iniai.cloud/webhook/survey-data?survey_id=${surveyId}`, {
        method: 'GET',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      console.log('Survey data received:', data);
      console.log('Is array?', Array.isArray(data));
      console.log('First item:', data[0]);

      // Unwrap array if data comes back wrapped
      const unwrappedData = Array.isArray(data) ? data[0] : data;
      console.log('Has survey_overview?', !!unwrappedData?.survey_overview);

      setSurveyData(unwrappedData);
    } catch (err) {
      console.error('Error fetching survey analysis:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (sentiment) => {
    const lower = sentiment?.toLowerCase() || '';
    if (lower.includes('positive')) return '#10b981';
    if (lower.includes('negative')) return '#ef4444';
    return '#f59e0b';
  };

  const getSentimentBadgeClass = (sentiment) => {
    const lower = sentiment?.toLowerCase() || '';
    if (lower.includes('positive')) return 'sentiment-positive';
    if (lower.includes('negative')) return 'sentiment-negative';
    return 'sentiment-neutral';
  };

  const toggleQuote = (index) => {
    setExpandedQuotes(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <div className="persona-interview-container">
        <div className="loading-state">
          <Loader2 className="spin" size={48} />
          <p>Loading survey analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !surveyData || !surveyData.survey_overview) {
    return (
      <div className="persona-interview-container">
        <div className="error-state">
          <AlertCircle size={48} />
          <h3>Error Loading Survey</h3>
          <p>{error || 'Survey data not found or invalid structure'}</p>
          <button onClick={() => navigate('/persona-survey-history')} className="run-survey-btn">
            <ArrowLeft size={16} />
            Back to History
          </button>
        </div>
      </div>
    );
  }

  const {
    survey_overview,
    text_questions = [],
    scale_questions = [],
    select_questions = [],
    text_responses = [],
    scale_responses = [],
    select_responses = [],
    questions_metadata = [],
    demographic_insights = []
  } = surveyData;

  // Extract unique demographic values from response data
  const getDemographicOptions = () => {
    const locations = new Set();
    const ageRanges = new Set();
    const incomes = new Set();
    const profiles = new Set();

    // Extract from text responses
    text_responses.forEach(response => {
      if (response.location_type) locations.add(response.location_type);
      if (response.age_range) ageRanges.add(response.age_range);
      if (response.income_bracket) incomes.add(response.income_bracket);
      if (response.profile_name) profiles.add(response.profile_name);
    });

    // Extract from scale responses
    scale_responses.forEach(response => {
      if (response.location_type) locations.add(response.location_type);
      if (response.age_range) ageRanges.add(response.age_range);
      if (response.income_bracket) incomes.add(response.income_bracket);
      if (response.profile_name) profiles.add(response.profile_name);
    });

    // Extract from select responses
    select_responses.forEach(response => {
      if (response.location_type) locations.add(response.location_type);
      if (response.age_range) ageRanges.add(response.age_range);
      if (response.income_bracket) incomes.add(response.income_bracket);
      if (response.profile_name) profiles.add(response.profile_name);
    });

    return {
      locations: Array.from(locations).sort(),
      ageRanges: Array.from(ageRanges).sort(),
      incomes: Array.from(incomes).sort(),
      profiles: Array.from(profiles).sort()
    };
  };

  const demographicOptions = getDemographicOptions();

  // Apply demographic filters
  const applyFilters = (responses) => {
    if (!responses) return [];

    return responses.filter(response => {
      if (demographicFilters.location_type !== 'all' && response.location_type !== demographicFilters.location_type) {
        return false;
      }
      if (demographicFilters.age_range !== 'all' && response.age_range !== demographicFilters.age_range) {
        return false;
      }
      if (demographicFilters.income_bracket !== 'all' && response.income_bracket !== demographicFilters.income_bracket) {
        return false;
      }
      if (demographicFilters.profile_name !== 'all' && response.profile_name !== demographicFilters.profile_name) {
        return false;
      }
      return true;
    });
  };

  // Check if filters are active
  const hasActiveFilters =
    demographicFilters.location_type !== 'all' ||
    demographicFilters.age_range !== 'all' ||
    demographicFilters.income_bracket !== 'all' ||
    demographicFilters.profile_name !== 'all';

  const filteredTextResponses = applyFilters(text_responses);
  const filteredScaleResponses = applyFilters(scale_responses);
  const filteredSelectResponses = applyFilters(select_responses);

  // Rebuild questions with filtered responses
  const filteredTextQuestions = text_questions.map(q => {
    const questionResponses = filteredTextResponses.filter(r => r.question_id === q.question_id);
    return {
      ...q,
      filtered_response_count: questionResponses.length,
      original_response_count: text_responses.filter(r => r.question_id === q.question_id).length
    };
  });

  const filteredScaleQuestions = scale_questions.map(q => {
    const questionResponses = filteredScaleResponses.filter(r => r.question_id === q.question_id);

    if (questionResponses.length > 0) {
      const values = questionResponses.map(r => parseFloat(r.scale_response)).sort((a, b) => a - b);
      const sum = values.reduce((a, b) => a + b, 0);
      const avg = sum / values.length;
      const min = Math.min(...values);
      const max = Math.max(...values);
      const median = values.length % 2 === 0
        ? (values[values.length / 2 - 1] + values[values.length / 2]) / 2
        : values[Math.floor(values.length / 2)];
      const q1 = values[Math.floor(values.length * 0.25)];
      const q3 = values[Math.floor(values.length * 0.75)];

      // Recalculate distribution
      const distribution = {};
      values.forEach(v => { distribution[v] = (distribution[v] || 0) + 1; });

      return {
        ...q,
        filtered_scale_average: avg.toFixed(2),
        filtered_scale_min: min,
        filtered_scale_max: max,
        filtered_scale_median: median,
        filtered_scale_q1: q1,
        filtered_scale_q3: q3,
        filtered_scale_distribution: distribution,
        filtered_response_count: questionResponses.length,
        original_response_count: scale_responses.filter(r => r.question_id === q.question_id).length
      };
    }

    return {
      ...q,
      filtered_response_count: 0,
      original_response_count: scale_responses.filter(r => r.question_id === q.question_id).length
    };
  });

  // Rebuild select questions with filtered responses
  const filteredSelectQuestions = select_questions.map(q => {
    const questionResponses = filteredSelectResponses.filter(r => r.question_id === q.question_id);

    if (questionResponses.length > 0 && hasActiveFilters) {
      // Recalculate distribution from filtered responses
      const optionCounts = {};
      questionResponses.forEach(r => {
        const option = r.selected_option;
        if (option) optionCounts[option] = (optionCounts[option] || 0) + 1;
      });
      const totalResponses = questionResponses.length;
      const optionDistribution = {};
      let mostPopular = { option: '', count: 0 };
      Object.entries(optionCounts).forEach(([option, count]) => {
        const percentage = parseFloat(((count / totalResponses) * 100).toFixed(1));
        optionDistribution[option] = { count, percentage };
        if (count > mostPopular.count) mostPopular = { option, count };
      });

      return {
        ...q,
        select_distribution: {
          ...q.select_distribution,
          option_distribution: optionDistribution,
          most_popular_option: mostPopular.option
        },
        total_responses: totalResponses,
        filtered_response_count: questionResponses.length,
        original_response_count: select_responses.filter(r => r.question_id === q.question_id).length
      };
    }

    return {
      ...q,
      filtered_response_count: questionResponses.length,
      original_response_count: select_responses.filter(r => r.question_id === q.question_id).length
    };
  });

  // Navigate to question from demographic highlight
  const handleHighlightClick = (insight) => {
    setBreakdownDimension(insight.segment_type);

    const inScale = scale_questions.some(q => q.question_id === insight.question_id);
    const inSelect = select_questions.some(q => q.question_id === insight.question_id);
    const inText = text_questions.some(q => q.question_id === insight.question_id);

    if (inScale) {
      setActiveTab('scale');
      const idx = scale_questions.findIndex(q => q.question_id === insight.question_id);
      if (idx >= 0) setSelectedScaleQuestion(idx);
    } else if (inSelect) {
      setActiveTab('select');
      const idx = select_questions.findIndex(q => q.question_id === insight.question_id);
      if (idx >= 0) setSelectedSelectQuestion(idx);
    } else if (inText) {
      setActiveTab('text');
      const idx = text_questions.findIndex(q => q.question_id === insight.question_id);
      if (idx >= 0) setSelectedTextQuestion(idx);
    }

    setTimeout(() => {
      document.querySelector('.question-analysis-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  return (
    <div className="persona-interview-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-top">
            <button onClick={() => navigate('/persona-survey-history')} className="back-button">
              <ArrowLeft size={20} />
            </button>
            <div className="header-title">
              <h1>{survey_overview.survey_name}</h1>
            </div>
            <button
              className={`filter-toggle-btn ${hasActiveFilters ? 'has-active' : ''}`}
              onClick={() => setShowFilterBar(prev => !prev)}
              title="Toggle filters"
            >
              <Filter size={16} />
              {hasActiveFilters && <span className="filter-active-dot" />}
            </button>
          </div>
          <p className="survey-description">{survey_overview.total_personas} personas · {survey_overview.positive_percentage}% positive · {text_questions.length + scale_questions.length + select_questions.length} questions · {new Date(survey_overview.generated_at).toLocaleDateString()}</p>
        </div>
      </div>

      {/* Filter Responses Bar — collapsible */}
      {(demographicOptions.ageRanges.length > 0 || demographicOptions.incomes.length > 0 || demographicOptions.locations.length > 0 || demographicOptions.profiles.length > 0) && (
        <div className={`filter-responses-bar ${showFilterBar || hasActiveFilters ? 'expanded' : 'collapsed'}`}>
          <span className="filter-bar-label">
            <Filter size={14} />
            Filter Responses
          </span>
          <div className="filter-bar-dropdowns">
            {demographicOptions.ageRanges.length > 0 && (
              <div className="filter-bar-group">
                <label>Age</label>
                <select
                  value={demographicFilters.age_range}
                  onChange={(e) => setDemographicFilters({...demographicFilters, age_range: e.target.value})}
                >
                  <option value="all">All Ages</option>
                  {demographicOptions.ageRanges.map(age => (
                    <option key={age} value={age}>{age}</option>
                  ))}
                </select>
              </div>
            )}
            {demographicOptions.incomes.length > 0 && (
              <div className="filter-bar-group">
                <label>Income</label>
                <select
                  value={demographicFilters.income_bracket}
                  onChange={(e) => setDemographicFilters({...demographicFilters, income_bracket: e.target.value})}
                >
                  <option value="all">All Incomes</option>
                  {demographicOptions.incomes.map(income => (
                    <option key={income} value={income}>{income}</option>
                  ))}
                </select>
              </div>
            )}
            {demographicOptions.locations.length > 0 && (
              <div className="filter-bar-group">
                <label>Location</label>
                <select
                  value={demographicFilters.location_type}
                  onChange={(e) => setDemographicFilters({...demographicFilters, location_type: e.target.value})}
                >
                  <option value="all">All Locations</option>
                  {demographicOptions.locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
            )}
            {demographicOptions.profiles.length > 0 && (
              <div className="filter-bar-group">
                <label>Profile</label>
                <select
                  value={demographicFilters.profile_name}
                  onChange={(e) => setDemographicFilters({...demographicFilters, profile_name: e.target.value})}
                >
                  <option value="all">All Profiles</option>
                  {demographicOptions.profiles.map(profile => (
                    <option key={profile} value={profile}>{profile}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {hasActiveFilters && (
            <div className="filter-bar-status">
              <span className="filter-bar-count">
                Showing {filteredTextResponses.length + filteredScaleResponses.length + filteredSelectResponses.length} of {text_responses.length + scale_responses.length + select_responses.length} responses
              </span>
              <button
                className="clear-filters-btn"
                onClick={() => setDemographicFilters({ location_type: 'all', age_range: 'all', income_bracket: 'all', profile_name: 'all' })}
              >
                <X size={14} />
                Clear All
              </button>
            </div>
          )}
        </div>
      )}

      {/* Key Findings */}
      <div className="key-findings-section">
        {/* Strategic Insights — hero content */}
        {survey_overview.key_insight && (
          <div className="key-insight-card" style={{ marginTop: 0 }}>
            <div className="insight-header">
              <Lightbulb size={24} />
              <h3>Strategic Insights</h3>
            </div>
            <div
              className="insight-content"
              dangerouslySetInnerHTML={{ __html: formatMarkdown(survey_overview.key_insight) }}
            />
          </div>
        )}

        {/* Top Themes */}
        {survey_overview.top_3_themes?.length > 0 && (
          <div className="themes-summary">
            <h3 className="subsection-title">
              <TrendingUp size={18} />
              Top Themes
            </h3>
            <div className="themes-grid">
              {survey_overview.top_3_themes.map((theme, index) => (
                <div key={index} className="theme-card">
                  <h4>{theme.theme}</h4>
                  <div className="theme-mentions">
                    <Users size={14} />
                    <span>{theme.mentions} mentions</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notable Segments — compact list */}
        {(() => {
          const notableInsights = demographic_insights
            .filter(i => i.notable_pattern && i.notable_pattern !== 'null')
            .sort((a, b) => b.response_count - a.response_count);
          if (notableInsights.length < 2) return null;

          return (
            <div className="demographic-callouts">
              <h4 className="callouts-label">Notable Segments</h4>
              <div className="callouts-list">
                {notableInsights.slice(0, 3).map((insight, idx) => {
                  const displayOrder = questions_metadata?.find(
                    m => m.question_id === insight.question_id && m.display_order
                  )?.display_order;
                  const qLabel = displayOrder ? `Q${displayOrder}` : '';

                  return (
                    <button
                      key={idx}
                      className="callout-item"
                      onClick={() => handleHighlightClick(insight)}
                    >
                      <span className="callout-pattern">
                        {insight.response_count} {insight.segment_value} respondent{insight.response_count !== 1 ? 's' : ''} averaged {insight.scale_average || 'N/A'}
                        {qLabel && ` on ${qLabel}`}
                      </span>
                      <span className="callout-segment-tag">
                        {insight.segment_type.replace('_', ' ')}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Question Analysis Tabs */}
      <div className="question-analysis-section">
        <div className="tabs-header">
          <div className="tabs-buttons">
            <button
              className={`tab-button text-type ${activeTab === 'text' ? 'active' : ''}`}
              onClick={() => setActiveTab('text')}
            >
              <MessageSquare size={18} />
              Text ({text_questions.length})
            </button>
            <button
              className={`tab-button scale-type ${activeTab === 'scale' ? 'active' : ''}`}
              onClick={() => setActiveTab('scale')}
            >
              <BarChart3 size={18} />
              Scale ({scale_questions.length})
            </button>
            <button
              className={`tab-button select-type ${activeTab === 'select' ? 'active' : ''}`}
              onClick={() => setActiveTab('select')}
            >
              <CheckCircle size={18} />
              Select ({select_questions.length})
            </button>
          </div>
        </div>

        {activeTab === 'text' && filteredTextQuestions.length > 0 && (
          <div className="tab-content">
            <TextQuestionsTab
              questions={filteredTextQuestions}
              responses={filteredTextResponses}
              allResponses={text_responses}
              selectedQuestion={selectedTextQuestion}
              setSelectedQuestion={setSelectedTextQuestion}
              expandedQuotes={expandedQuotes}
              toggleQuote={toggleQuote}
              getSentimentBadgeClass={getSentimentBadgeClass}
              hasActiveFilters={hasActiveFilters}
              questionsMetadata={questions_metadata}
              breakdownDimension={breakdownDimension}
              setBreakdownDimension={setBreakdownDimension}
              demographicOptions={demographicOptions}
            />
          </div>
        )}

        {activeTab === 'scale' && filteredScaleQuestions.length > 0 && (
          <div className="tab-content">
            <ScaleQuestionsTab
              questions={filteredScaleQuestions}
              responses={filteredScaleResponses}
              allResponses={scale_responses}
              selectedQuestion={selectedScaleQuestion}
              setSelectedQuestion={setSelectedScaleQuestion}
              hasActiveFilters={hasActiveFilters}
              questionsMetadata={questions_metadata}
              breakdownDimension={breakdownDimension}
              setBreakdownDimension={setBreakdownDimension}
              customGroups={customGroups}
              setCustomGroups={setCustomGroups}
              expandedSegmentRows={expandedSegmentRows}
              setExpandedSegmentRows={setExpandedSegmentRows}
              demographicOptions={demographicOptions}
              surveyId={surveyId}
            />
          </div>
        )}

        {activeTab === 'select' && filteredSelectQuestions.length > 0 && (
          <div className="tab-content">
            <SelectQuestionsTab
              questions={filteredSelectQuestions}
              responses={filteredSelectResponses}
              allResponses={select_responses}
              selectedQuestion={selectedSelectQuestion}
              setSelectedQuestion={setSelectedSelectQuestion}
              questionsMetadata={questions_metadata}
              hasActiveFilters={hasActiveFilters}
              breakdownDimension={breakdownDimension}
              setBreakdownDimension={setBreakdownDimension}
              customGroups={customGroups}
              setCustomGroups={setCustomGroups}
              expandedSegmentRows={expandedSegmentRows}
              setExpandedSegmentRows={setExpandedSegmentRows}
              demographicOptions={demographicOptions}
              surveyId={surveyId}
            />
          </div>
        )}
      </div>

    </div>
  );
}

// Shared COMPARE BY Chips — renders pill-shaped toggles for each demographic dimension
function CompareByChips({ responses, demographicOptions, breakdownDimension, setBreakdownDimension }) {
  const dims = buildDimensions(responses, demographicOptions);
  if (dims.length === 0) return null;

  // Extract the base dimension key (before :: for presets/custom)
  const activeDimKey = breakdownDimension ? breakdownDimension.split('::')[0] : '';

  return (
    <div className="compare-by-chips">
      <span className="compare-by-label">Compare by:</span>
      {dims.map(dim => {
        const isActive = activeDimKey === dim.key;
        return (
          <button
            key={dim.key}
            className={`compare-chip ${isActive ? 'active' : ''}`}
            onClick={() => setBreakdownDimension(isActive ? '' : dim.key)}
          >
            {dim.label}
          </button>
        );
      })}
    </div>
  );
}

// Text Questions Tab Component
function TextQuestionsTab({ questions, responses, allResponses, selectedQuestion, setSelectedQuestion, expandedQuotes, toggleQuote, getSentimentBadgeClass, hasActiveFilters, questionsMetadata, breakdownDimension, setBreakdownDimension, demographicOptions }) {
  const currentQuestion = questions[selectedQuestion];

  // Get display order for a question
  const getDisplayOrder = (questionId) => {
    const meta = questionsMetadata?.find(m => m.question_id === questionId && m.display_order);
    return meta?.display_order || null;
  };

  // Filter quotes to only show those matching the filtered responses
  const filteredQuotes = hasActiveFilters && currentQuestion.representative_quotes
    ? currentQuestion.representative_quotes.filter(quote => {
        // Check if this quote's persona is in the filtered responses
        return responses.some(r =>
          r.question_id === currentQuestion.question_id &&
          r.profile_name === quote.persona
        );
      })
    : currentQuestion.representative_quotes;

  const sentimentData = currentQuestion.sentiment_breakdown ? [
    { name: 'Positive', value: parseFloat(currentQuestion.sentiment_breakdown.positive || 0), color: '#10b981' },
    { name: 'Neutral', value: parseFloat(currentQuestion.sentiment_breakdown.neutral || 0), color: '#f59e0b' },
    { name: 'Negative', value: parseFloat(currentQuestion.sentiment_breakdown.negative || 0), color: '#ef4444' }
  ].filter(item => item.value > 0) : [];

  return (
    <div className="text-questions-content">
      <CompareByChips
        responses={allResponses}
        demographicOptions={demographicOptions}
        breakdownDimension={breakdownDimension}
        setBreakdownDimension={setBreakdownDimension}
      />

      <div className="question-selector">
        <label>Select Question</label>
        <select
          value={selectedQuestion}
          onChange={(e) => setSelectedQuestion(parseInt(e.target.value))}
          className="question-dropdown"
        >
          {questions.map((q, idx) => {
            const displayOrder = getDisplayOrder(q.question_id);
            return (
              <option key={idx} value={idx}>
                Q{displayOrder || idx + 1}: {q.question_text}
              </option>
            );
          })}
        </select>
      </div>

      <div className="sentiment-section">
        <h3 className="subsection-title">Sentiment Breakdown</h3>
        <div className="sentiment-container">
          <div className="sentiment-chart-wrapper">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={sentimentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={110}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {sentimentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value}%`}
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="sentiment-legend">
            {sentimentData.map((item, idx) => (
              <div key={idx} className="sentiment-legend-item">
                <div className="sentiment-legend-color" style={{ background: item.color }}></div>
                <div className="sentiment-legend-content">
                  <div className="sentiment-legend-label">{item.name}</div>
                  <div className="sentiment-legend-value">{item.value}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="themes-section">
        <h3 className="subsection-title">Common Themes</h3>
        <table className="themes-table">
          <thead>
            <tr>
              <th>Theme</th>
              <th>Mentions</th>
              <th>Sentiment</th>
            </tr>
          </thead>
          <tbody>
            {currentQuestion.common_themes?.sort((a, b) => b.mentions - a.mentions).map((theme, idx) => (
              <tr key={idx}>
                <td className="theme-name">{theme.theme}</td>
                <td className="theme-mentions">
                  <span className="mentions-badge">{theme.mentions}</span>
                </td>
                <td className="theme-sentiment">
                  <span className={`sentiment-badge ${getSentimentBadgeClass(theme.sentiment)}`}>
                    {theme.sentiment}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="quotes-section">
        <h3 className="subsection-title">Representative Quotes</h3>
        <div className="quotes-grid">
          {filteredQuotes?.map((quote, idx) => {
            const isExpanded = expandedQuotes[`${selectedQuestion}-${idx}`];
            const shouldTruncate = quote.text.length > 400;
            const displayText = isExpanded || !shouldTruncate ? quote.text : quote.text.substring(0, 400) + '...';

            return (
              <div key={idx} className="quote-card">
                <div className="quote-content">
                  <div className="quote-text">
                    {displayText}
                    {shouldTruncate && !isExpanded && (
                      <button
                        onClick={() => toggleQuote(`${selectedQuestion}-${idx}`)}
                        className="read-more-inline"
                      >
                        Read more
                      </button>
                    )}
                  </div>
                  {shouldTruncate && isExpanded && (
                    <button
                      onClick={() => toggleQuote(`${selectedQuestion}-${idx}`)}
                      className="read-more-btn"
                    >
                      Show less
                    </button>
                  )}
                </div>

                {quote.why_selected && (
                  <div className="quote-reason">
                    <strong>Why this quote matters</strong>
                    {quote.why_selected}
                  </div>
                )}

                <div className="quote-meta">
                  <div className="quote-persona">
                    <Users size={18} />
                    <span>{quote.persona}</span>
                  </div>
                  {quote.index !== undefined && (
                    <span className="quote-index">Response #{quote.index + 1}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Box Plot Component
function BoxPlot({ min, q1, median, q3, max, average, scaleRange = [1, 5] }) {
  const width = 800;
  const height = 120;
  const margin = { top: 20, right: 60, bottom: 40, left: 60 };
  const plotWidth = width - margin.left - margin.right;

  // Scale function to map data values to pixel positions
  const scale = (value) => {
    return margin.left + ((value - scaleRange[0]) / (scaleRange[1] - scaleRange[0])) * plotWidth;
  };

  const boxHeight = 40;
  const boxY = margin.top + 20;

  // Calculate variance for interpretation
  const range = max - min;
  const interpretation = range <= 1
    ? `Tight clustering indicates strong consensus (${Math.round(((q3 - q1) / (scaleRange[1] - scaleRange[0])) * 100)}% of responses between ${q1}-${q3})`
    : range <= 2
    ? `Moderate consensus with most responses between ${q1}-${q3}`
    : `Wide distribution of responses across the scale`;

  return (
    <div className="boxplot-container">
      <div className="boxplot-interpretation">{interpretation}</div>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Scale axis */}
        <line
          x1={margin.left}
          y1={boxY + boxHeight/2}
          x2={width - margin.right}
          y2={boxY + boxHeight/2}
          stroke="#cbd5e1"
          strokeWidth="1"
        />

        {/* Tick marks and labels */}
        {[1, 2, 3, 4, 5].map(tick => (
          <g key={tick}>
            <line
              x1={scale(tick)}
              y1={boxY + boxHeight/2 - 5}
              x2={scale(tick)}
              y2={boxY + boxHeight/2 + 5}
              stroke="#64748b"
              strokeWidth="1.5"
            />
            <text
              x={scale(tick)}
              y={boxY + boxHeight/2 + 25}
              textAnchor="middle"
              fill="#64748b"
              fontSize="12"
              fontWeight="500"
            >
              {tick}
            </text>
          </g>
        ))}

        {/* Whiskers */}
        <line
          x1={scale(min)}
          y1={boxY + boxHeight/2}
          x2={scale(q1)}
          y2={boxY + boxHeight/2}
          stroke="#8b5cf6"
          strokeWidth="2"
        />
        <line
          x1={scale(q3)}
          y1={boxY + boxHeight/2}
          x2={scale(max)}
          y2={boxY + boxHeight/2}
          stroke="#8b5cf6"
          strokeWidth="2"
        />

        {/* Min/Max caps */}
        <line
          x1={scale(min)}
          y1={boxY + boxHeight/2 - 10}
          x2={scale(min)}
          y2={boxY + boxHeight/2 + 10}
          stroke="#8b5cf6"
          strokeWidth="2"
        />
        <line
          x1={scale(max)}
          y1={boxY + boxHeight/2 - 10}
          x2={scale(max)}
          y2={boxY + boxHeight/2 + 10}
          stroke="#8b5cf6"
          strokeWidth="2"
        />

        {/* Box (IQR) */}
        <rect
          x={scale(q1)}
          y={boxY}
          width={scale(q3) - scale(q1)}
          height={boxHeight}
          fill="#8b5cf6"
          fillOpacity="0.3"
          stroke="#8b5cf6"
          strokeWidth="2"
          rx="4"
        />

        {/* Median line */}
        <line
          x1={scale(median)}
          y1={boxY}
          x2={scale(median)}
          y2={boxY + boxHeight}
          stroke="#8b5cf6"
          strokeWidth="3"
        />

        {/* Average line */}
        <line
          x1={scale(average)}
          y1={boxY - 5}
          x2={scale(average)}
          y2={boxY + boxHeight + 5}
          stroke="#ef4444"
          strokeWidth="2.5"
          strokeDasharray="4 4"
        />
        <text
          x={scale(average)}
          y={boxY - 12}
          textAnchor="middle"
          fill="#ef4444"
          fontSize="11"
          fontWeight="600"
        >
          Avg: {average}
        </text>

        {/* Axis label */}
        <text
          x={width / 2}
          y={height - 5}
          textAnchor="middle"
          fill="#475569"
          fontSize="12"
          fontWeight="600"
        >
          Scale Rating
        </text>
      </svg>
    </div>
  );
}

// Build dimension definitions from responses
function buildDimensions(responses, demographicOptions) {
  const dims = [
    { key: 'age_range', label: 'Age Range', values: demographicOptions?.ageRanges || [] },
    { key: 'income_bracket', label: 'Income Bracket', values: demographicOptions?.incomes || [] },
    { key: 'location_type', label: 'Location Type', values: demographicOptions?.locations || [] },
    { key: 'gender', label: 'Gender', values: [...new Set(responses.map(r => r.gender).filter(Boolean))].sort() }
  ].filter(d => d.values.length > 1);
  return dims;
}

// Preset group definitions
function getPresetGroups(dimensionKey) {
  const presets = {
    age_range: [
      { label: 'Under 35 / 35+', groups: { 'Under 35': ['18-24', '25-34'], '35+': ['35-44', '45-54', '55-64', '65+'] } },
      { label: 'By decade', groups: null } // null = raw values
    ],
    income_bracket: [
      { label: 'Under 50k / 50k+', groups: { 'Under 50k': ['<30k', '30k-50k', '<$30k', '$30k-$50k'], '50k+': ['50k-75k', '75k-100k', '100k+', '$50k-$75k', '$75k-$100k', '$100k+'] } },
      { label: 'By bracket', groups: null }
    ]
  };
  return presets[dimensionKey] || [];
}

// Compute most interesting (highest variance) dimension
function getMostInterestingDimension(responses, questionId, dimensions, responseValueFn) {
  let maxVariance = -1;
  let bestDim = '';

  dimensions.forEach(dim => {
    const groupMeans = {};
    const groupCounts = {};
    responses.filter(r => r.question_id === questionId).forEach(r => {
      const group = r[dim.key];
      if (!group) return;
      const val = responseValueFn(r);
      if (val === null) return;
      groupMeans[group] = (groupMeans[group] || 0) + val;
      groupCounts[group] = (groupCounts[group] || 0) + 1;
    });

    const means = Object.keys(groupMeans).map(g => groupMeans[g] / groupCounts[g]);
    if (means.length < 2) return;

    const overallMean = means.reduce((a, b) => a + b, 0) / means.length;
    const variance = means.reduce((sum, m) => sum + Math.pow(m - overallMean, 2), 0) / means.length;

    if (variance > maxVariance) {
      maxVariance = variance;
      bestDim = dim.key;
    }
  });

  return bestDim;
}

// SegmentBreakdown Component - shared between Scale and Select tabs
function SegmentBreakdown({ responses, allResponses, questionId, questionType, dimensions, breakdownDimension, setBreakdownDimension, customGroups, setCustomGroups, expandedSegmentRows, setExpandedSegmentRows, surveyId }) {
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [customBuilderDim, setCustomBuilderDim] = useState('');
  const [customBuilderGroups, setCustomBuilderGroups] = useState([]);

  if (!dimensions || dimensions.length === 0) return null;

  const questionResponses = allResponses.filter(r => r.question_id === questionId);
  if (questionResponses.length === 0) return null;

  // Parse the selected breakdown option
  const parseBreakdownSelection = (value) => {
    if (!value) return { dimension: '', grouping: null };
    const parts = value.split('::');
    if (parts.length === 2) {
      return { dimension: parts[0], grouping: parts[1] };
    }
    return { dimension: value, grouping: null };
  };

  // Build groups from selection
  const buildGroups = () => {
    const parsed = parseBreakdownSelection(breakdownDimension);
    if (!parsed.dimension) return [];

    const dim = dimensions.find(d => d.key === parsed.dimension);
    if (!dim) return [];

    // Check if custom groups are defined for this dimension
    const customKey = `${parsed.dimension}::custom`;
    if (parsed.grouping === 'custom' && customGroups[customKey]) {
      return customGroups[customKey].map(g => ({
        label: g.label,
        values: g.values,
        responses: questionResponses.filter(r => g.values.includes(r[parsed.dimension]))
      }));
    }

    // Check preset groups
    if (parsed.grouping) {
      const presets = getPresetGroups(parsed.dimension);
      const preset = presets.find(p => p.label === parsed.grouping);
      if (preset && preset.groups) {
        return Object.entries(preset.groups).map(([label, values]) => ({
          label,
          values,
          responses: questionResponses.filter(r => values.includes(r[parsed.dimension]))
        })).filter(g => g.responses.length > 0);
      }
    }

    // Default: raw values
    return dim.values.map(val => ({
      label: val,
      values: [val],
      responses: questionResponses.filter(r => r[parsed.dimension] === val)
    })).filter(g => g.responses.length > 0);
  };

  const groups = buildGroups();

  // Toggle row expansion
  const toggleRow = (groupLabel) => {
    setExpandedSegmentRows(prev => ({
      ...prev,
      [`${questionId}::${groupLabel}`]: !prev[`${questionId}::${groupLabel}`]
    }));
  };

  // Custom group builder handlers
  const addCustomGroup = () => {
    setCustomBuilderGroups([...customBuilderGroups, { label: '', values: [] }]);
  };

  const updateCustomGroupLabel = (idx, label) => {
    const updated = [...customBuilderGroups];
    updated[idx].label = label;
    setCustomBuilderGroups(updated);
  };

  const toggleCustomGroupValue = (idx, value) => {
    const updated = [...customBuilderGroups];
    const vals = updated[idx].values;
    if (vals.includes(value)) {
      updated[idx].values = vals.filter(v => v !== value);
    } else {
      updated[idx].values = [...vals, value];
    }
    setCustomBuilderGroups(updated);
  };

  const saveCustomGroups = () => {
    const customKey = `${customBuilderDim}::custom`;
    setCustomGroups(prev => ({
      ...prev,
      [customKey]: customBuilderGroups.filter(g => g.label && g.values.length > 0)
    }));
    setBreakdownDimension(`${customBuilderDim}::custom`);
    setShowCustomBuilder(false);
    setExpandedSegmentRows({});
  };

  // Don't render if no dimension selected via COMPARE BY chips
  if (!breakdownDimension) return null;

  const parsed = parseBreakdownSelection(breakdownDimension);
  const activeDimKey = parsed.dimension;
  const activeDim = dimensions.find(d => d.key === activeDimKey);
  const activePresets = getPresetGroups(activeDimKey);
  const currentGrouping = parsed.grouping; // null = raw values, string = preset label or 'custom'

  return (
    <div className="segment-breakdown">
      {/* Grouping options strip — presets + custom for active dimension */}
      {(activePresets.length > 0 || activeDim) && (
        <div className="grouping-strip">
          <span className="grouping-strip-label">Group:</span>
          <button
            className={`grouping-option ${!currentGrouping ? 'active' : ''}`}
            onClick={() => { setBreakdownDimension(activeDimKey); setExpandedSegmentRows({}); }}
          >
            Each value
          </button>
          {activePresets.filter(p => p.groups).map(preset => (
            <button
              key={preset.label}
              className={`grouping-option ${currentGrouping === preset.label ? 'active' : ''}`}
              onClick={() => { setBreakdownDimension(`${activeDimKey}::${preset.label}`); setExpandedSegmentRows({}); }}
            >
              {preset.label}
            </button>
          ))}
          <button
            className={`grouping-option ${currentGrouping === 'custom' ? 'active' : ''}`}
            onClick={() => {
              setShowCustomBuilder(true);
              setCustomBuilderDim(activeDimKey);
              setCustomBuilderGroups(customGroups[`${activeDimKey}::custom`] || [{ label: '', values: [] }]);
            }}
          >
            Custom...
          </button>
        </div>
      )}

      {/* Custom group builder modal */}
      {showCustomBuilder && (
        <div className="custom-group-builder">
          <div className="custom-builder-header">
            <h4>Custom Grouping</h4>
            <button className="custom-builder-close" onClick={() => setShowCustomBuilder(false)}>
              <X size={16} />
            </button>
          </div>
          <div className="custom-builder-dim-select">
            <label>Dimension:</label>
            <select value={customBuilderDim} onChange={(e) => {
              setCustomBuilderDim(e.target.value);
              setCustomBuilderGroups([{ label: '', values: [] }]);
            }}>
              {dimensions.map(d => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </div>
          {customBuilderGroups.map((group, gIdx) => {
            const dim = dimensions.find(d => d.key === customBuilderDim);
            return (
              <div key={gIdx} className="custom-group-row">
                <input
                  type="text"
                  placeholder="Group name..."
                  value={group.label}
                  onChange={(e) => updateCustomGroupLabel(gIdx, e.target.value)}
                  className="custom-group-name-input"
                />
                <div className="custom-group-values">
                  {dim?.values.map(val => (
                    <label key={val} className="custom-group-checkbox">
                      <input
                        type="checkbox"
                        checked={group.values.includes(val)}
                        onChange={() => toggleCustomGroupValue(gIdx, val)}
                      />
                      <span>{val}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="custom-builder-actions">
            <button className="custom-builder-add" onClick={addCustomGroup}>+ Add Group</button>
            <button className="custom-builder-save" onClick={saveCustomGroups}>Apply</button>
          </div>
        </div>
      )}

      {/* Breakdown table for scale questions */}
      {groups.length > 0 && questionType === 'scale' && (
        <div className="breakdown-table-container">
          <table className="breakdown-table">
            <thead>
              <tr>
                <th>Group</th>
                <th>Avg</th>
                <th>N</th>
                <th>Distribution</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => {
                const values = group.responses.map(r => parseFloat(r.scale_response)).filter(v => !isNaN(v));
                const avg = values.length > 0 ? (values.reduce((a, b) => a + b, 0) / values.length) : 0;
                const isExpanded = expandedSegmentRows[`${questionId}::${group.label}`];
                const maxPossibleAvg = 5;

                return (
                  <React.Fragment key={group.label}>
                    <tr className="breakdown-row" onClick={() => toggleRow(group.label)}>
                      <td className="breakdown-group-cell">
                        <span className="breakdown-expand-icon">
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </span>
                        {group.label}
                      </td>
                      <td className="breakdown-avg-cell">{values.length > 0 ? avg.toFixed(1) : '-'}</td>
                      <td className="breakdown-n-cell">{values.length}</td>
                      <td className="breakdown-bar-cell">
                        <div className="breakdown-bar-track">
                          <div
                            className="breakdown-bar-fill"
                            style={{ width: `${(avg / maxPossibleAvg) * 100}%` }}
                          />
                        </div>
                      </td>
                      <td className="breakdown-arrow-cell">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="breakdown-expanded-row">
                        <td colSpan={5}>
                          <div className="breakdown-personas">
                            {group.responses.map((r, rIdx) => (
                              <div key={rIdx} className="breakdown-persona-row">
                                <span className="persona-name">{r.profile_name || r.persona_name || 'Unknown'}</span>
                                <span className="persona-demo">{r.location_type}</span>
                                <span className="persona-demo">{r.income_bracket}</span>
                                <span className="persona-score">scored: {r.scale_response}</span>
                                <a
                                  href={`/persona-interview?persona_id=${encodeURIComponent(r.persona_id || r.profile_name)}&survey_context=${encodeURIComponent(surveyId || '')}`}
                                  className="interview-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Interview →
                                </a>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Breakdown for select questions */}
      {groups.length > 0 && (questionType === 'single_select' || questionType === 'multi_select') && (
        <div className="breakdown-select-container">
          {(() => {
            // Gather all unique options across groups
            const allOptions = [...new Set(groups.flatMap(g =>
              g.responses.map(r => r.selected_option).filter(Boolean)
            ))].sort();

            return allOptions.map(option => (
              <div key={option} className="breakdown-select-option">
                <div className="breakdown-select-option-label">{option}</div>
                <div className="breakdown-select-bars">
                  {groups.map(group => {
                    const total = group.responses.length;
                    const count = group.responses.filter(r => r.selected_option === option).length;
                    const pct = total > 0 ? ((count / total) * 100) : 0;
                    return (
                      <div key={group.label} className="breakdown-select-bar-row">
                        <span className="breakdown-select-group-label">{group.label}</span>
                        <div className="breakdown-bar-track">
                          <div className="breakdown-bar-fill select-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="breakdown-select-pct">{pct.toFixed(0)}% ({count}/{total})</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}

          {/* Expandable persona rows for select */}
          {groups.map(group => {
            const isExpanded = expandedSegmentRows[`${questionId}::${group.label}`];
            return (
              <div key={group.label} className="breakdown-select-group-expand">
                <button className="breakdown-group-toggle" onClick={() => toggleRow(group.label)}>
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span>{group.label} ({group.responses.length})</span>
                </button>
                {isExpanded && (
                  <div className="breakdown-personas">
                    {group.responses.map((r, rIdx) => (
                      <div key={rIdx} className="breakdown-persona-row">
                        <span className="persona-name">{r.profile_name || r.persona_name || 'Unknown'}</span>
                        <span className="persona-demo">{r.location_type}</span>
                        <span className="persona-demo">{r.income_bracket}</span>
                        <span className="persona-selected">selected: {r.selected_option}</span>
                        <a
                          href={`/persona-interview?persona_id=${encodeURIComponent(r.persona_id || r.profile_name)}&survey_context=${encodeURIComponent(surveyId || '')}`}
                          className="interview-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Interview →
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Scale Questions Tab Component
function ScaleQuestionsTab({ questions, responses, allResponses, selectedQuestion, setSelectedQuestion, hasActiveFilters, questionsMetadata, breakdownDimension, setBreakdownDimension, customGroups, setCustomGroups, expandedSegmentRows, setExpandedSegmentRows, demographicOptions, surveyId }) {
  const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'detail'
  const currentQuestion = questions[selectedQuestion];

  // Auto-select highest-variance dimension on first load
  useEffect(() => {
    if (!breakdownDimension && questions.length > 0 && allResponses.length > 0) {
      const dims = buildDimensions(allResponses, demographicOptions);
      if (dims.length > 0) {
        const best = getMostInterestingDimension(
          allResponses, questions[0].question_id, dims, r => parseFloat(r.scale_response)
        );
        if (best) setBreakdownDimension(best);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get display order for a question
  const getDisplayOrder = (questionId) => {
    const meta = questionsMetadata?.find(m => m.question_id === questionId && m.display_order);
    return meta?.display_order || null;
  };

  // Use filtered stats if filters are active, otherwise use original stats
  const useFiltered = hasActiveFilters && currentQuestion.filtered_scale_average;
  const displayStats = {
    average: useFiltered ? currentQuestion.filtered_scale_average : currentQuestion.scale_average,
    min: useFiltered ? currentQuestion.filtered_scale_min : currentQuestion.scale_min,
    max: useFiltered ? currentQuestion.filtered_scale_max : currentQuestion.scale_max,
    median: useFiltered ? currentQuestion.filtered_scale_median : currentQuestion.scale_median,
    mode: currentQuestion.scale_mode,
    stddev: currentQuestion.scale_stddev,
    q1: useFiltered ? currentQuestion.filtered_scale_q1 : currentQuestion.scale_q1,
    q3: useFiltered ? currentQuestion.filtered_scale_q3 : currentQuestion.scale_q3
  };

  const getStdDevColor = (stddev) => {
    const val = parseFloat(stddev);
    if (val < 0.5) return '#10b981'; // green
    if (val <= 1.0) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  const getConsensusLabel = (stddev) => {
    const val = parseFloat(stddev);
    if (val < 0.5) return 'High Consensus';
    if (val <= 1.0) return 'Moderate';
    return 'High Variance';
  };

  const getConsensusInsight = (question) => {
    const range = parseFloat(question.scale_max) - parseFloat(question.scale_min);
    if (range === 0) {
      return `100% responses at ${question.scale_max}`;
    }
    if (range <= 1) {
      return `All responses ${question.scale_min}-${question.scale_max}`;
    }
    return `Spread ${question.scale_min}-${question.scale_max}`;
  };

  // Mini distribution chart component
  const MiniDistribution = ({ distribution, scaleRange = [1, 5] }) => {
    if (!distribution) return null;

    const data = Object.entries(distribution).map(([scale, count]) => ({
      scale: parseInt(scale),
      count: parseInt(count)
    }));

    const maxCount = Math.max(...data.map(d => d.count));

    return (
      <div className="mini-distribution">
        {data.map((item, idx) => {
          const height = (item.count / maxCount) * 100;
          return (
            <div key={idx} className="mini-bar-wrapper">
              <div
                className="mini-bar"
                style={{ height: `${height}%` }}
                title={`${item.scale}: ${item.count} responses`}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="scale-questions-content">
      <CompareByChips
        responses={allResponses}
        demographicOptions={demographicOptions}
        breakdownDimension={breakdownDimension}
        setBreakdownDimension={setBreakdownDimension}
      />

      <div className="view-mode-toggle">
        <button
          className={`mode-button ${viewMode === 'overview' ? 'active' : ''}`}
          onClick={() => setViewMode('overview')}
        >
          Overview Grid
        </button>
        <button
          className={`mode-button ${viewMode === 'detail' ? 'active' : ''}`}
          onClick={() => setViewMode('detail')}
        >
          Individual Question
        </button>
      </div>

      {viewMode === 'overview' ? (
        <div className="scale-overview-section">
          <h3 className="subsection-title">All Scale Questions Overview</h3>

          <div className="scale-overview-table-container">
            <table className="scale-overview-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Avg</th>
                  <th>StdDev</th>
                  <th>Min-Max</th>
                  <th>Distribution</th>
                  {breakdownDimension && <th>By {buildDimensions(allResponses, demographicOptions).find(d => d.key === breakdownDimension.split('::')[0])?.label}</th>}
                  <th>Insight</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {questions.map((q, idx) => {
                  const range = parseFloat(q.scale_max) - parseFloat(q.scale_min);
                  const isPerfectConsensus = range === 0;
                  const displayOrder = getDisplayOrder(q.question_id);
                  return (
                    <tr
                      key={idx}
                      className="scale-overview-row"
                      onClick={() => {
                        setSelectedQuestion(idx);
                        setViewMode('detail');
                      }}
                    >
                      <td className="question-text-cell">
                        <span className="question-number">Q{displayOrder || idx + 1}</span>
                        <span className="question-text" style={{ whiteSpace: 'pre-line' }}>
                          {q.question_text?.replace(/\\n/g, '\n') || q.question_text}
                        </span>
                      </td>
                      <td className="stat-cell">
                        <span className="stat-value">{q.scale_average}</span>
                      </td>
                      <td className="stat-cell">
                        <span
                          className="stddev-badge"
                          style={{
                            backgroundColor: `${getStdDevColor(q.scale_stddev)}20`,
                            color: getStdDevColor(q.scale_stddev),
                            border: `1.5px solid ${getStdDevColor(q.scale_stddev)}`
                          }}
                        >
                          {q.scale_stddev}
                        </span>
                        <div className="stddev-label" style={{ color: getStdDevColor(q.scale_stddev) }}>
                          {getConsensusLabel(q.scale_stddev)}
                        </div>
                      </td>
                      <td className="stat-cell">
                        <div className="range-display">
                          <span className="range-value">{q.scale_min}–{q.scale_max}</span>
                          {isPerfectConsensus && <span className="consensus-check">✓</span>}
                        </div>
                      </td>
                      <td className="distribution-cell">
                        <MiniDistribution distribution={q.scale_distribution} />
                      </td>
                      {breakdownDimension && (
                        <td className="overview-breakdown-cell">
                          {(() => {
                            const baseDimKey = breakdownDimension.split('::')[0];
                            const dim = buildDimensions(allResponses, demographicOptions).find(d => d.key === baseDimKey);
                            if (!dim) return '-';
                            const qResps = allResponses.filter(r => r.question_id === q.question_id);
                            return dim.values.map(val => {
                              const groupResps = qResps.filter(r => r[baseDimKey] === val);
                              if (groupResps.length === 0) return null;
                              const avg = groupResps.reduce((s, r) => s + parseFloat(r.scale_response), 0) / groupResps.length;
                              return (
                                <span key={val} className="overview-breakdown-item">
                                  {val}: {avg.toFixed(1)}
                                </span>
                              );
                            }).filter(Boolean);
                          })()}
                        </td>
                      )}
                      <td className="insight-cell">
                        <span className="insight-text">{getConsensusInsight(q)}</span>
                      </td>
                      <td className="action-cell">
                        <span className="drill-arrow">→</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <div className="question-selector">
            <label>Select Question</label>
            <select
              value={selectedQuestion}
              onChange={(e) => setSelectedQuestion(parseInt(e.target.value))}
              className="question-dropdown"
            >
              {questions.map((q, idx) => {
                const displayOrder = getDisplayOrder(q.question_id);
                return (
                  <option key={idx} value={idx}>
                    Q{displayOrder || idx + 1}: {q.question_text}
                  </option>
                );
              })}
            </select>
          </div>

      <div className="scale-stats-grid">
        <div className="scale-stat-card">
          <div className="scale-stat-label">Average</div>
          <div className="scale-stat-value">{displayStats.average}</div>
        </div>
        <div className="scale-stat-card">
          <div className="scale-stat-label">Median</div>
          <div className="scale-stat-value">{displayStats.median}</div>
        </div>
        <div className="scale-stat-card">
          <div className="scale-stat-label">Mode</div>
          <div className="scale-stat-value">{displayStats.mode}</div>
        </div>
        <div className="scale-stat-card">
          <div className="scale-stat-label">Std Dev</div>
          <div className="scale-stat-value">{displayStats.stddev}</div>
        </div>
      </div>

      <div className="distribution-section">
        <h3 className="subsection-title">Response Distribution</h3>
        <BoxPlot
          min={parseFloat(displayStats.min)}
          q1={parseFloat(displayStats.q1)}
          median={parseFloat(displayStats.median)}
          q3={parseFloat(displayStats.q3)}
          max={parseFloat(displayStats.max)}
          average={parseFloat(displayStats.average)}
          scaleRange={[1, 5]}
        />
      </div>

      {/* Segment Breakdown — directly after chart */}
      <SegmentBreakdown
        responses={responses}
        allResponses={allResponses}
        questionId={currentQuestion.question_id}
        questionType="scale"
        dimensions={buildDimensions(allResponses, demographicOptions)}
        breakdownDimension={breakdownDimension}
        setBreakdownDimension={setBreakdownDimension}
        customGroups={customGroups}
        setCustomGroups={setCustomGroups}
        expandedSegmentRows={expandedSegmentRows}
        setExpandedSegmentRows={setExpandedSegmentRows}
        surveyId={surveyId}
      />

      <div className="quartile-section">
        <h3 className="subsection-title">Quartile Breakdown</h3>
        <div className="quartile-grid">
          <div className="quartile-item">
            <span className="quartile-label">Min</span>
            <span className="quartile-value">{displayStats.min}</span>
          </div>
          <div className="quartile-item">
            <span className="quartile-label">Q1 (25%)</span>
            <span className="quartile-value">{displayStats.q1}</span>
          </div>
          <div className="quartile-item">
            <span className="quartile-label">Q2 (50%)</span>
            <span className="quartile-value">{displayStats.median}</span>
          </div>
          <div className="quartile-item">
            <span className="quartile-label">Q3 (75%)</span>
            <span className="quartile-value">{displayStats.q3}</span>
          </div>
          <div className="quartile-item">
            <span className="quartile-label">Max</span>
            <span className="quartile-value">{displayStats.max}</span>
          </div>
        </div>
      </div>

      <div className="response-count">
        <Users size={20} />
        <span>{currentQuestion.total_responses} total responses</span>
      </div>
        </>
      )}
    </div>
  );
}

// Select Questions Tab Component
function SelectQuestionsTab({ questions, responses, allResponses, selectedQuestion, setSelectedQuestion, questionsMetadata, hasActiveFilters, breakdownDimension, setBreakdownDimension, customGroups, setCustomGroups, expandedSegmentRows, setExpandedSegmentRows, demographicOptions, surveyId }) {
  const currentQuestion = questions[selectedQuestion];
  const isMultiSelect = currentQuestion?.question_type === 'multi_select';

  // Get display order for a question
  const getDisplayOrder = (questionId) => {
    const meta = questionsMetadata?.find(m => m.question_id === questionId && m.display_order);
    return meta?.display_order || null;
  };

  // Prepare data for charts
  const chartData = currentQuestion?.select_distribution?.option_distribution
    ? Object.entries(currentQuestion.select_distribution.option_distribution).map(([option, data]) => ({
        option,
        count: data.count,
        percentage: data.percentage
      })).sort((a, b) => b.count - a.count)
    : [];


  return (
    <div className="select-questions-content">
      <CompareByChips
        responses={allResponses}
        demographicOptions={demographicOptions}
        breakdownDimension={breakdownDimension}
        setBreakdownDimension={setBreakdownDimension}
      />

      <div className="question-selector">
        <label>Select Question</label>
        <select
          value={selectedQuestion}
          onChange={(e) => setSelectedQuestion(parseInt(e.target.value))}
          className="question-dropdown"
        >
          {questions.map((q, idx) => {
            const displayOrder = getDisplayOrder(q.question_id);
            return (
              <option key={idx} value={idx}>
                Q{displayOrder || idx + 1}: {q.question_text}
              </option>
            );
          })}
        </select>
      </div>

      <div className="question-type-badge" style={{
        background: isMultiSelect ? '#f97316' : '#10b981',
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '8px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1rem',
        fontSize: '0.875rem',
        fontWeight: '600'
      }}>
        <CheckCircle size={16} />
        {isMultiSelect ? 'Multi-Select' : 'Single-Select'} Question
        {isMultiSelect && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', opacity: 0.9 }}>
          (Percentages can exceed 100%)
        </span>}
      </div>

      <div className="select-summary">
        <div className="stat-card" style={{ flex: 1 }}>
          <div className="stat-icon" style={{ background: '#10b98120' }}>
            <Users size={24} style={{ color: '#10b981' }} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{currentQuestion?.total_responses || 0}</div>
            <div className="stat-label">Total Responses</div>
          </div>
        </div>

        {currentQuestion?.select_distribution?.most_popular_option && (
          <div className="stat-card" style={{ flex: 2 }}>
            <div className="stat-icon" style={{ background: '#3b82f620' }}>
              <TrendingUp size={24} style={{ color: '#3b82f6' }} />
            </div>
            <div className="stat-content">
              <div className="stat-value" style={{ fontSize: '1.1rem' }}>
                {currentQuestion.select_distribution.most_popular_option}
              </div>
              <div className="stat-label">Most Popular Option</div>
            </div>
          </div>
        )}
      </div>

      {/* Chart Section */}
      <div className="distribution-section" style={{ marginTop: '2rem' }}>
        <h3 className="subsection-title">Response Distribution</h3>

        {isMultiSelect ? (
          // Horizontal bar chart for multi-select
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 50)}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                <YAxis
                  type="category"
                  dataKey="option"
                  width={150}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'percentage') return [`${value}%`, 'Percentage'];
                    return [value, 'Count'];
                  }}
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}
                />
                <Legend />
                <Bar dataKey="percentage" fill="#f97316" name="Percentage" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          // Vertical bar chart for single-select
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="option"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fontSize: 11 }}
              />
              <YAxis tickFormatter={(value) => `${value}%`} />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'percentage') return [`${value}%`, 'Percentage'];
                  return [value, 'Count'];
                }}
                contentStyle={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '8px 12px'
                }}
              />
              <Legend />
              <Bar dataKey="percentage" fill="#10b981" name="Percentage" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Segment Breakdown — directly after chart */}
      <SegmentBreakdown
        responses={responses}
        allResponses={allResponses}
        questionId={currentQuestion?.question_id}
        questionType={currentQuestion?.question_type || 'single_select'}
        dimensions={buildDimensions(allResponses, demographicOptions)}
        breakdownDimension={breakdownDimension}
        setBreakdownDimension={setBreakdownDimension}
        customGroups={customGroups}
        setCustomGroups={setCustomGroups}
        expandedSegmentRows={expandedSegmentRows}
        setExpandedSegmentRows={setExpandedSegmentRows}
        surveyId={surveyId}
      />

      {/* Detailed breakdown table */}
      <div className="themes-section" style={{ marginTop: '2rem' }}>
        <h3 className="subsection-title">Detailed Breakdown</h3>
        <table className="themes-table">
          <thead>
            <tr>
              <th>Option</th>
              <th>Count</th>
              <th>Percentage</th>
              <th>Visual</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((item, idx) => (
              <tr key={idx}>
                <td className="theme-name">{item.option}</td>
                <td className="theme-mentions">
                  <span className="mentions-badge">{item.count}</span>
                </td>
                <td>
                  <strong>{item.percentage}%</strong>
                </td>
                <td>
                  <div style={{
                    width: '100%',
                    maxWidth: '200px',
                    height: '8px',
                    background: '#e2e8f0',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${item.percentage}%`,
                      height: '100%',
                      background: isMultiSelect ? '#f97316' : '#10b981',
                      borderRadius: '4px'
                    }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SurveyAnalysisDashboard;
